import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Send, ArrowRight, Users, Building2, Dumbbell, Menu, X } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    type: 'general',
    message: '',
  });

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
    input: { width: '100%', padding: '14px 16px', borderRadius: '8px', border: '1px solid #dadce0', fontSize: '16px', fontFamily: 'inherit', transition: 'border-color 0.2s', boxSizing: 'border-box' as const, outline: 'none' },
    select: { width: '100%', padding: '14px 16px', borderRadius: '8px', border: '1px solid #dadce0', fontSize: '16px', fontFamily: 'inherit', background: '#ffffff', boxSizing: 'border-box' as const, outline: 'none', cursor: 'pointer' },
    textarea: { width: '100%', padding: '14px 16px', borderRadius: '8px', border: '1px solid #dadce0', fontSize: '16px', fontFamily: 'inherit', minHeight: '130px', resize: 'vertical' as const, boxSizing: 'border-box' as const, outline: 'none' },
  };

  const contactChannels = [
    { icon: <Users size={24} color="#14B8A6" />, title: 'General Support', subtitle: 'For users & general queries', email: 'support@huslrz.com', link: 'mailto:support@huslrz.com' },
    { icon: <Dumbbell size={24} color="#14B8A6" />, title: 'Partner Enquiries', subtitle: 'For gym, turf & studio owners', email: 'support@huslrz.com', link: 'mailto:support@huslrz.com' },
    { icon: <Building2 size={24} color="#14B8A6" />, title: 'Corporate Wellness', subtitle: 'For HR & procurement teams', email: 'support@huslrz.com', link: 'mailto:support@huslrz.com' },
  ];

  return (
    <>
      <Helmet>
        <title>Contact HUSLRZ — Get in Touch</title>
        <meta name="description" content="Reach out to HUSLRZ for support, partnership enquiries, or corporate wellness plans. We're based in Chennai and happy to help." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124', minHeight: '100vh', background: '#ffffff' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>Get Started <ArrowRight size={16} /></Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>Get Started <ArrowRight size={18} /></Link>
            </div>
          </>
        )}

        {/* Hero */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', textAlign: 'center' as const }}>
          <div style={styles.container}>
            <h1 style={{ fontSize: 'clamp(32px, 7vw, 56px)', fontWeight: 400, color: '#202124', marginBottom: '16px', letterSpacing: '-0.5px' }}>
              Get in touch
            </h1>
            <p style={{ fontSize: isMobile ? '16px' : '20px', color: '#5f6368', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
              Whether you're a user, facility owner, or corporate HR team — we're here to help.
            </p>
          </div>
        </section>

        {/* Contact Channels */}
        <section style={{ padding: isMobile ? '0 16px 40px' : '0 24px 48px' }}>
          <div style={styles.container}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '20px' }}>
              {contactChannels.map((ch, i) => (
                <a key={i} href={ch.link} style={{ background: '#f8f9fa', borderRadius: '16px', padding: isMobile ? '24px' : '32px', textAlign: 'center' as const, textDecoration: 'none', transition: 'all 0.2s', border: '1px solid transparent' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>{ch.icon}</div>
                  <h3 style={{ fontSize: '17px', fontWeight: 500, color: '#202124', marginBottom: '4px' }}>{ch.title}</h3>
                  <p style={{ fontSize: '13px', color: '#5f6368', margin: '0 0 12px' }}>{ch.subtitle}</p>
                  <div style={{ fontSize: '15px', color: '#14B8A6', fontWeight: 600 }}>{ch.email}</div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form + Office Info */}
        <section style={{ padding: isMobile ? '0 16px 60px' : '0 24px 80px' }}>
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '48px' }}>
              {/* Form */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: isMobile ? '24px' : '36px', border: '1px solid #e8eaed' }}>
                <h2 style={{ fontSize: '22px', fontWeight: 500, color: '#202124', marginBottom: '24px' }}>
                  Send us a message
                </h2>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '6px' }}>Name *</label>
                    <input type="text" required style={styles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '6px' }}>Email *</label>
                    <input type="email" required style={styles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '6px' }}>Phone</label>
                    <input type="tel" style={styles.input} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '6px' }}>I am a... *</label>
                    <select style={styles.select} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                      <option value="general">Fitness Enthusiast / User</option>
                      <option value="partner">Gym / Turf / Studio Owner</option>
                      <option value="corporate">Corporate / HR Team</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '6px' }}>Message *</label>
                    <textarea required style={styles.textarea} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us how we can help..." />
                  </div>
                  <button type="submit" style={{ width: '100%', background: '#14B8A6', color: '#ffffff', padding: '16px', borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    Send Message <Send size={18} />
                  </button>
                </form>
              </div>

              {/* Info Sidebar */}
              <div>
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <MapPin size={20} color="#14B8A6" />
                    <h3 style={{ fontSize: '17px', fontWeight: 500, color: '#202124', margin: 0 }}>Office</h3>
                  </div>
                  <p style={{ fontSize: '15px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>
                    Chennai, Tamil Nadu, India
                  </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <Mail size={20} color="#14B8A6" />
                    <h3 style={{ fontSize: '17px', fontWeight: 500, color: '#202124', margin: 0 }}>Email Directly</h3>
                  </div>
                  <div style={{ fontSize: '15px', color: '#5f6368', lineHeight: 2 }}>
                    <div><strong>All enquiries:</strong> <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a></div>
                  </div>
                </div>

                <div style={{ background: '#f8f9fa', borderRadius: '16px', padding: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>Response Times</h3>
                  <div style={{ fontSize: '14px', color: '#5f6368', lineHeight: 2 }}>
                    <div>General queries: within 24 hours</div>
                    <div>Partner enquiries: within 12 hours</div>
                    <div>Corporate demos: within 4 hours</div>
                    <div>Urgent / payment issues: within 2 hours</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '64px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px' : '40px', marginBottom: '40px' }}>
              <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#14B8A6', marginBottom: '12px' }}>HUSLRZ</div>
                <p style={{ fontSize: '14px', color: '#9CA3AF', lineHeight: 1.6, margin: 0 }}>India's pay-per-use fitness marketplace. Fitness without commitment.</p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Platform</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/partners" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Partners</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/corporate" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Corporates</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/about" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>About Us</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Legal</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Get in Touch</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px', fontSize: '14px', color: '#9CA3AF' }}>support@huslrz.com</li>
                  <li style={{ fontSize: '14px', color: '#9CA3AF' }}>Chennai, Tamil Nadu</li>
                </ul>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactPage;
